import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import { drawTextOverlay, ensureFontsLoaded } from './textOverlayRenderer';

// Output frame for a video story — 9:16, authored at 1080×1920 (same as the image path).
export const OUT_W = 1080;
export const OUT_H = 1920;
export const OUT_FPS = 30;
const REST_TMS = 10_000_000; // render overlays at their animation rest state

/** Cover-fit a source (sw×sh) into a dest (dw×dh): fill the frame, center-crop the overflow. */
export const coverRect = (sw, sh, dw, dh) => {
  const scale = Math.max(dw / sw, dh / sh);
  const w = sw * scale;
  const h = sh * scale;
  return { x: (dw - w) / 2, y: (dh - h) / 2, w, h };
};

/** Number of output frames for a duration (ms) at a frame rate. */
export const frameCount = (durationMs, fps = OUT_FPS) => Math.max(1, Math.ceil((durationMs / 1000) * fps));

/**
 * Composite a video story — the source video cover-fit to 9:16 plus the text overlays burned in —
 * into an mp4 Blob.
 *
 * Uses WebCodecs (VideoEncoder + mp4-muxer), so it runs only in a real browser, not jsdom. The pure
 * helpers above (coverRect, frameCount) are unit-tested; this orchestration is validated in-browser.
 * Frames are pulled by seeking the video element, which keeps this standalone (no decoder worker /
 * video-editor coupling).
 *
 * @param videoEl  a loaded, seekable HTMLVideoElement (crossOrigin set so the canvas isn't tainted)
 * @param layers   text overlay objects (textOverlayRenderer schema)
 * @param opts     { onProgress(percent), background }
 * @returns Blob (video/mp4)
 */
export const exportVideoStory = async (videoEl, layers, { onProgress, background } = {}) => {
  if (typeof VideoEncoder === 'undefined' || typeof VideoFrame === 'undefined') {
    throw new Error('This browser can’t export video stories (no WebCodecs support).');
  }
  await ensureFontsLoaded();

  const srcW = videoEl.videoWidth;
  const srcH = videoEl.videoHeight;
  if (!srcW || !srcH || !videoEl.duration) throw new Error('The video isn’t ready to export yet.');

  const total = frameCount(videoEl.duration * 1000);
  const cover = coverRect(srcW, srcH, OUT_W, OUT_H);
  const box = { x: 0, y: 0, w: OUT_W, h: OUT_H };

  const canvas = new OffscreenCanvas(OUT_W, OUT_H);
  const ctx = canvas.getContext('2d');

  const target = new ArrayBufferTarget();
  const muxer = new Muxer({ target, video: { codec: 'avc', width: OUT_W, height: OUT_H }, fastStart: 'in-memory' });
  let encodeError = null;
  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (e) => { encodeError = e; },
  });
  encoder.configure({
    codec: 'avc1.42001f', width: OUT_W, height: OUT_H,
    bitrate: 8_000_000, framerate: OUT_FPS, hardwareAcceleration: 'prefer-hardware',
  });

  const frameDurUs = Math.round(1_000_000 / OUT_FPS);
  const seekTo = (t) => new Promise((resolve) => {
    const onSeeked = () => { videoEl.removeEventListener('seeked', onSeeked); resolve(); };
    videoEl.addEventListener('seeked', onSeeked);
    videoEl.currentTime = Math.min(Math.max(0, t), Math.max(0, videoEl.duration - 0.001));
  });

  try {
    for (let i = 0; i < total; i++) {
      if (encodeError) throw encodeError;
      await seekTo(i / OUT_FPS);
      ctx.clearRect(0, 0, OUT_W, OUT_H);
      if (background?.type === 'solid') { ctx.fillStyle = background.color; ctx.fillRect(0, 0, OUT_W, OUT_H); }
      ctx.drawImage(videoEl, cover.x, cover.y, cover.w, cover.h);
      layers.forEach((l) => drawTextOverlay(ctx, l, box, REST_TMS));
      const frame = new VideoFrame(canvas, { timestamp: i * frameDurUs, duration: frameDurUs });
      encoder.encode(frame);
      frame.close();
      onProgress?.(Math.round(((i + 1) / total) * 100));
    }
    await encoder.flush();
    if (encodeError) throw encodeError;
    muxer.finalize();
    return new Blob([target.buffer], { type: 'video/mp4' });
  } finally {
    try { encoder.close(); } catch { /* already closed */ }
  }
};
